import { OrganizationDetailsResponse, OrgFormValue, Organization, OrgPayload, PlanSelectionDetails } from '@core/models/org.model';
import { Address } from '@core/models/common.model';
import { OrderSummaryRequest } from '@core/models/order-summary.model';
import { PaymentCapturePayload } from '@core/models/subscription.model';
import { UserProfile, UserProfileWithOTP } from '@core/models/user.model';
import { APP_ROUTES } from '@core/constants/route.constant';
import { toBillingCycleEnum } from '@shared/utils/pricing.utils';

export type EnterpriseRouteStep =
  | 'list'
  | 'details'
  | 'add-user'
  | 'edit-user'
  | 'add-organization'
  | 'edit-organization'
  | 'add-plan'
  | 'edit-plan'
  | 'order-summary'
  | 'payment-capture'
  | 'onboarding-success'
  | 'verify-otp';

export const ENTERPRISE_STATUS_FILTERS = [
  'ALL',
  'PENDING SUBSCRIPTION',
  'PENDING ACTIVATION',
  'PENDING PAYMENT',
  'SCHEDULED',
  'ACTIVE',
] as const;

export const ENTERPRISE_SORT_OPTIONS = [
  { value: 'nameAsc', label: 'Name (A–Z)' },
  { value: 'nameDesc', label: 'Name (Z–A)' },
  { value: 'createdAtAsc', label: 'Created (oldest)' },
  { value: 'createdAtDesc', label: 'Created (newest)' },
] as const;

export type EnterpriseSortOption = (typeof ENTERPRISE_SORT_OPTIONS)[number]['value'];

export function buildEnterpriseRouteSegments(
  step: EnterpriseRouteStep,
  id?: string | number,
): string[] {
  const base = [APP_ROUTES.ADMIN.ROOT, APP_ROUTES.ADMIN.ENTERPRICES.ROOT];

  switch (step) {
    case 'list':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.LIST];
    case 'details':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.DETAILS, String(id)];
    case 'add-user':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.ADD_USER];
    case 'edit-user':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.EDIT_USER];
    case 'add-organization':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.ADD_ORGANIZATION];
    case 'edit-organization':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.EDIT_ORGANIZATION];
    case 'add-plan':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.ADD_PLAN];
    case 'edit-plan':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.EDIT_PLAN];
    case 'order-summary':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.ORDER_SUMMARY];
    case 'payment-capture':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.PAYMENT_CAPTURE];
    case 'onboarding-success':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.ONBOARDING_SUCCESS];
    case 'verify-otp':
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.VERIFY_OTP];
    default:
      return [...base, APP_ROUTES.ADMIN.ENTERPRICES.LIST];
  }
}

export function getOrgId(org: OrganizationDetailsResponse): string {
  return org.organizationResponse?.organizationId || '';
}

export function getOrgInitials(org: OrganizationDetailsResponse): string {
  const name = org.organizationResponse?.organizationName || '';
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

export function getOrganizationStatus(org: OrganizationDetailsResponse): string {
  return org.organizationResponse?.status || 'Unknown';
}

export function getSubscriptionStatus(org: OrganizationDetailsResponse): string {
  if (org.subscriptionResponse?.subscriptionStatus) {
    return org.subscriptionResponse.subscriptionStatus;
  }
  return 'PENDING SUBSCRIPTION';
}

export function getStatusClass(status: string): string {
  switch (status.toLowerCase()) {
    case 'active':
      return 'enterprise-status--active';
    case 'pending':
    case 'pending subscription':
    case 'pending activation':
    case 'pending payment':
      return 'enterprise-status--pending';
    case 'terminated':
      return 'enterprise-status--terminated';
    default:
      return 'enterprise-status--unknown';
  }
}

export function getAdminEmail(org: OrganizationDetailsResponse): string {
  return org.userResponse?.profileIdentifiers?.[0]?.emailId || '—';
}

export function getPlanLabel(org: OrganizationDetailsResponse): string {
  return (
    org.subscriptionResponse?.planDetails?.planName ||
    org.subscriptionResponse?.planDetails?.planId ||
    'No plan'
  );
}

export type EnterpriseListAction = 'activate' | 'payment' | 'plan' | 'view';

export function isEnterpriseTrial(org: OrganizationDetailsResponse): boolean {
  return !!org.subscriptionResponse?.planDetails?.trialFlag;
}

export function getEnterpriseListAction(org: OrganizationDetailsResponse): EnterpriseListAction {
  const subscriptionStatus = getSubscriptionStatus(org);
  const orgStatus = getOrganizationStatus(org);
  const trialFlag = org.subscriptionResponse?.planDetails?.trialFlag;
  const planType = org.subscriptionResponse?.planType;

  if (
    (subscriptionStatus === 'ACTIVE' && orgStatus === 'PENDING') ||
    (subscriptionStatus === 'PENDING' &&
      orgStatus === 'PENDING' &&
      (planType === 'OF' || trialFlag === true))
  ) {
    return 'activate';
  }

  if (subscriptionStatus === 'PENDING' && !trialFlag && planType === 'OP') {
    return 'payment';
  }

  if (subscriptionStatus === 'PENDING' && trialFlag && orgStatus === 'ACTIVE') {
    return 'plan';
  }

  return 'view';
}

export function planRenewalDate(org: OrganizationDetailsResponse): string | null {
  const planEndDate = org.subscriptionResponse?.planDetails?.planEndDate;
  if (!planEndDate) {
    return null;
  }

  const date = new Date(planEndDate);
  date.setDate(date.getDate() + 1);
  return date.toISOString();
}

export function formatEnterpriseCurrency(currency: string | undefined, amount: unknown): string {
  const symbol = currency === 'INR' ? '₹' : '$';
  return `${symbol}${amount ?? '—'}`;
}

export function formatDisplayLabel(value: string | null | undefined): string {
  if (!value?.trim()) {
    return '—';
  }

  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatOrganizationAddress(address?: Address | null): string {
  if (!address) {
    return '—';
  }

  const parts = [
    address.addressLine1,
    address.addressLine2,
    [address.city, address.state].filter(Boolean).join(', '),
    [address.country, address.pinCode].filter(Boolean).join(' '),
  ].filter((part) => !!part?.trim());

  return parts.length ? parts.join(' · ') : '—';
}

export function mapUserToFormPatch(user: UserProfile | undefined): Record<string, unknown> {
  if (!user) {
    return {};
  }

  return {
    firstName: user.firstName,
    lastName: user.lastName,
    emailId: user.profileIdentifiers?.[0]?.emailId || '',
    phoneNumber: user.mobileNumber,
    mobileNumber: user.mobileNumber,
    displayName: user.displayName,
    language: user.language,
    timeZone: user.timeZone,
    gender: user.gender,
    addressType: user.address?.addressType,
    addressLine1: user.address?.addressLine1,
    addressLine2: user.address?.addressLine2,
    city: user.address?.city,
    state: user.address?.state,
    country: user.address?.country,
    pinCode: user.address?.pinCode,
  };
}

export function buildUserUpdatePayload(
  event: Record<string, unknown>,
  existing: UserProfile,
): UserProfile {
  const mobileNumber = String(
    event['mobileNumber'] ?? event['phoneNumber'] ?? existing.mobileNumber ?? '',
  ).trim();

  return {
    ...existing,
    firstName: String(event['firstName'] || existing.firstName || '').trim(),
    lastName: String(event['lastName'] || existing.lastName || '').trim(),
    displayName: String(event['displayName'] || existing.displayName || '').trim(),
    mobileNumber,
    language: String(event['language'] || existing.language || ''),
    timeZone: String(event['timeZone'] || existing.timeZone || ''),
    gender: String(event['gender'] || existing.gender || ''),
    role: existing.role,
    profileIdentifiers: existing.profileIdentifiers,
    address: {
      addressType: String(event['addressType'] || existing.address?.addressType || 'O'),
      addressLine1: String(event['addressLine1'] || existing.address?.addressLine1 || '').trim(),
      addressLine2: String(event['addressLine2'] || existing.address?.addressLine2 || '').trim(),
      city: String(event['city'] || existing.address?.city || '').trim(),
      state: String(event['state'] || existing.address?.state || '').trim(),
      country: String(event['country'] || existing.address?.country || '').trim(),
      pinCode: String(event['pinCode'] || existing.address?.pinCode || '').trim(),
    },
  };
}

export function filterEnterprisesBySearch(
  list: OrganizationDetailsResponse[],
  query: string,
): OrganizationDetailsResponse[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return list;
  }

  return list.filter((org) =>
    org.organizationResponse?.organizationName?.toLowerCase().includes(normalized),
  );
}

export function filterEnterprisesByStatus(
  list: OrganizationDetailsResponse[],
  statusFilter: string,
): OrganizationDetailsResponse[] {
  if (!statusFilter || statusFilter === 'ALL') {
    return list;
  }

  return list.filter((org) => getSubscriptionStatus(org) === statusFilter);
}

export function sortEnterprises(
  list: OrganizationDetailsResponse[],
  sortOption: EnterpriseSortOption,
): OrganizationDetailsResponse[] {
  const sorted = [...list];

  if (sortOption === 'nameAsc') {
    return sorted.sort((a, b) =>
      (a.organizationResponse?.organizationName || '').localeCompare(
        b.organizationResponse?.organizationName || '',
      ),
    );
  }

  if (sortOption === 'nameDesc') {
    return sorted.sort((a, b) =>
      (b.organizationResponse?.organizationName || '').localeCompare(
        a.organizationResponse?.organizationName || '',
      ),
    );
  }

  if (sortOption === 'createdAtAsc') {
    return sorted.sort(
      (a, b) =>
        new Date(a.organizationResponse?.auditInfo?.createdOn || 0).getTime() -
        new Date(b.organizationResponse?.auditInfo?.createdOn || 0).getTime(),
    );
  }

  return sorted.sort(
    (a, b) =>
      new Date(b.organizationResponse?.auditInfo?.createdOn || 0).getTime() -
      new Date(a.organizationResponse?.auditInfo?.createdOn || 0).getTime(),
  );
}

export function buildOrgCreatePayload(event: OrgFormValue): OrgPayload {
  return {
    timeZone: event.timeZone,
    organizationName: event.organizationName,
    industry: event.industry,
    organizationType: event.organizationType,
    parentOrgId: event.parentOrgId || undefined,
    baseCurrency: event.baseCurrency,
    webSite: event.website,
    address: {
      addressType: event.addressType || 'O',
      addressLine1: event.addressLine1,
      addressLine2: event.addressLine2,
      city: event.city,
      state: event.state,
      country: event.country,
      pinCode: event.pinCode,
    },
  };
}

export function buildOrgUpdatePayload(event: OrgFormValue, existing: Organization): Organization {
  return {
    organizationId: existing.organizationId,
    organizationName: event.organizationName,
    organizationType: event.organizationType,
    parentOrgId: event.parentOrgId || undefined,
    industry: event.industry,
    webSite: event.website,
    baseCurrency: event.baseCurrency,
    timeZone: event.timeZone,
    language: existing.language,
    registrationNumber: existing.registrationNumber,
    taxId: existing.taxId,
    taxNumber: existing.taxNumber,
    organizationLogoPath: existing.organizationLogoPath,
    status: existing.status,
    auditInfo: existing.auditInfo,
    address: {
      addressType: event.addressType,
      addressLine1: event.addressLine1,
      addressLine2: event.addressLine2,
      city: event.city,
      state: event.state,
      country: event.country,
      pinCode: event.pinCode,
    },
  };
}

export function buildAdminUserRegisterPayload(event: {
  emailId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  password: string;
  termsAndConditions?: boolean;
}): import('@core/models/user.model').UserPayload {
  return {
    profileIdentifiers: [
      {
        providerId: '',
        registrationMethod: '',
        emailId: event.emailId,
        uid: '',
      },
    ],
    email: event.emailId,
    displayName: event.firstName,
    passWord: event.password,
    firstName: event.firstName,
    lastName: event.lastName,
    mobileNumber: event.phoneNumber,
    termsConditionsDetails: {
      tandcLink: 'dummy',
      tCAcceptedStatus: event.termsAndConditions ?? true,
    },
  };
}

export function normalizeRegisterUserWithOtp(data: unknown): UserProfileWithOTP | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const payload = data as UserProfileWithOTP;
  if (payload.otpDetails && payload.user) {
    return payload;
  }

  const nested = (data as { data?: UserProfileWithOTP }).data;
  if (nested?.otpDetails && nested?.user) {
    return nested;
  }

  return null;
}

export function buildOrderPatchValues(
  planDetails: PlanSelectionDetails,
  organizationDetails?: Organization | null,
  userProfile?: UserProfile | null,
): Record<string, unknown> | null {
  if (!planDetails) {
    return null;
  }

  const address = organizationDetails?.address || userProfile?.address;

  return {
    addressLine1: address?.addressLine1,
    addressLine2: address?.addressLine2,
    city: address?.city,
    state: address?.state,
    country: address?.country,
    pinCode: address?.pinCode,
    addressType: address?.addressType || 'O',
    planName: planDetails.selectedPlan?.planName,
    billingCycle: toBillingCycleEnum(planDetails.selectedDuration),
    orderAmount: planDetails.planPrice,
    netAmount: planDetails.planPrice,
    currency: planDetails.selectedCurrency,
    planId: planDetails.selectedPlan?.planId,
    taxCode: planDetails.selectedPlan?.taxDetails?.taxCode || 'GST',
    taxPerc: planDetails.selectedPlan?.taxDetails?.taxPerc || 18,
    taxAmount:
      (planDetails.planPrice * (planDetails.selectedPlan?.taxDetails?.taxPerc || 18)) / 100,
    totalPayableAmount:
      planDetails.planPrice +
      (planDetails.planPrice * (planDetails.selectedPlan?.taxDetails?.taxPerc || 18)) / 100,
    discount: 0,
  };
}

export function buildOrderSummaryRequest(
  userProfile: UserProfile,
  planDetails: PlanSelectionDetails,
  billing: {
    trialFlag: boolean;
    address: Address;
  },
): OrderSummaryRequest {
  return {
    customerId: userProfile.organizationId,
    planDetails: {
      planId: planDetails.selectedPlan.planId,
      trialFlag: billing.trialFlag,
      billingCycle: toBillingCycleEnum(planDetails.selectedDuration),
      autoDebitFlag: false,
      userCount: planDetails.userCount ?? 1,
      address: billing.address,
    },
  };
}

export function buildSubscriptionCreatePayload(
  userProfile: UserProfile,
  planDetails: PlanSelectionDetails,
  billing: Record<string, unknown>,
): import('@core/models/subscription.model').SubscriptionPayload {
  const address: Address = {
    addressType: 'B',
    addressLine1: ((billing['addressLine1'] as string) || '').trim(),
    addressLine2: (billing['addressLine2'] as string)?.trim(),
    city: ((billing['city'] as string) || '').trim(),
    state: ((billing['state'] as string) || '').trim(),
    country: ((billing['country'] as string) || '').trim(),
    pinCode: ((billing['pinCode'] as string) || '').trim(),
  };

  return {
    customerId: userProfile.organizationId,
    planDetails: {
      planId: planDetails.selectedPlan.planId,
      trialFlag: !!billing['trialFlag'],
      billingCycle: toBillingCycleEnum(planDetails.selectedDuration),
      userCount: planDetails.userCount ?? 1,
      autoDebitFlag: true,
      gstNumber: billing['addGstin'] ? ((billing['gstNumber'] as string) || undefined) : undefined,
      address,
    },
  };
}

export function buildPaymentCapturePayload(event: Record<string, unknown>): PaymentCapturePayload {
  return {
    modeOfPayment: event['modeOfPayment'] as string,
    transactionAmount: event['transactionAmount'] as number,
    transactionCurrency: event['transactionCurrency'] as string,
  };
}

/** @deprecated Use buildOrgCreatePayload or buildOrgUpdatePayload */
export function buildOrgPayload(value: OrgFormValue): OrgPayload {
  return buildOrgCreatePayload(value);
}

export function mapOrganizationToFormValue(org: Organization): OrgFormValue {
  return {
    organizationName: org.organizationName,
    organizationType: org.organizationType,
    parentOrgId: org.parentOrgId,
    industry: org.industry || '',
    website: org.webSite || '',
    baseCurrency: org.baseCurrency,
    timeZone: org.timeZone,
    language: org.language,
    addressType: org.address?.addressType || 'O',
    addressLine1: org.address?.addressLine1 || '',
    addressLine2: org.address?.addressLine2,
    city: org.address?.city || '',
    state: org.address?.state || '',
    country: org.address?.country || '',
    pinCode: org.address?.pinCode || '',
  };
}
